<script setup lang="ts">
import { onBeforeUnmount, ref, useId, watch } from 'vue'

/**
 * A small `?` affordance with a hover balloon.
 *
 * Hover alone is not enough: it excludes keyboard and touch users entirely. So the
 * trigger is a real button that also opens on focus and toggles on tap/Enter, and
 * the balloon is wired up with `aria-describedby` so screen readers announce it
 * with the control it explains.
 */
const props = defineProps<{
  /** Accessible name for the trigger, e.g. "What are reserved addresses?". */
  label: string
  /** The balloon's text. Keep it to a sentence or two. */
  text: string
}>()

const id = `infotip-${useId()}`
const pinned = ref(false)
const root = ref<HTMLElement | null>(null)
const bubble = ref<HTMLElement | null>(null)
/** Horizontal nudge, in px, that keeps an open balloon inside the viewport. */
const shift = ref(0)

/** Gap kept between the balloon and the viewport edge. */
const EDGE = 8

/**
 * Centres the balloon on the trigger, then slides it back inside the viewport if
 * that would cut it off — a `?` near the right edge of a phone screen otherwise
 * opens half off-screen. Its width does not depend on where it sits, so it can be
 * measured before it shows.
 */
function place(): void {
  if (!root.value || !bubble.value || typeof document === 'undefined') return
  const trigger = root.value.getBoundingClientRect()
  const width = bubble.value.offsetWidth
  const left = trigger.left + trigger.width / 2 - width / 2
  const right = left + width
  const viewport = document.documentElement.clientWidth
  if (left < EDGE) shift.value = EDGE - left
  else if (right > viewport - EDGE) shift.value = viewport - EDGE - right
  else shift.value = 0
}

function toggle(): void {
  pinned.value = !pinned.value
  if (pinned.value) place()
}

function onDocumentPointer(event: PointerEvent): void {
  if (root.value && !root.value.contains(event.target as Node)) pinned.value = false
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') pinned.value = false
}

// Listeners only exist while something is pinned open.
watch(pinned, (open) => {
  if (typeof document === 'undefined') return
  if (open) {
    document.addEventListener('pointerdown', onDocumentPointer)
    document.addEventListener('keydown', onKeydown)
  } else {
    document.removeEventListener('pointerdown', onDocumentPointer)
    document.removeEventListener('keydown', onKeydown)
  }
})

onBeforeUnmount(() => {
  if (typeof document === 'undefined') return
  document.removeEventListener('pointerdown', onDocumentPointer)
  document.removeEventListener('keydown', onKeydown)
})

void props
</script>

<template>
  <span
    ref="root"
    class="infotip"
    :class="{ 'infotip--pinned': pinned }"
    :style="{ '--infotip-shift': `${shift}px` }"
    @mouseenter="place()"
    @focusin="place()"
  >
    <button
      type="button"
      class="infotip__trigger"
      :aria-label="label"
      :aria-describedby="id"
      :aria-expanded="pinned"
      @click="toggle()"
    >
      <span aria-hidden="true">?</span>
    </button>
    <span :id="id" ref="bubble" class="infotip__bubble" role="tooltip">{{ text }}</span>
  </span>
</template>

<style scoped>
.infotip {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
}

.infotip__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.05rem;
  height: 1.05rem;
  padding: 0;
  border: 1px solid var(--color-border-strong);
  border-radius: 50%;
  background: transparent;
  color: var(--color-text-muted);
  font: inherit;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  cursor: help;
  transition:
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.infotip__trigger:hover,
.infotip__trigger:focus-visible,
.infotip--pinned .infotip__trigger {
  color: var(--color-accent);
  border-color: var(--color-accent);
}

/*
 * Closed, the balloon is anchored to grow leftwards from the trigger: overflow off
 * the left edge is never scrollable, so a hidden balloon near the right edge can
 * no longer widen the page. Open, it is centred on the trigger, nudged by
 * `--infotip-shift` to stay on screen.
 */
.infotip__bubble {
  position: absolute;
  bottom: calc(100% + 0.5rem);
  right: 0;
  z-index: 20;
  width: max-content;
  max-width: min(17rem, 70vw);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-raised);
  color: var(--color-text);
  box-shadow: var(--shadow-md);
  font-size: var(--text-sm);
  font-weight: 400;
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--transition-fast),
    visibility var(--transition-fast);
}

/* Opens on hover, on keyboard focus, and when tapped. */
.infotip:hover .infotip__bubble,
.infotip__trigger:focus-visible ~ .infotip__bubble,
.infotip--pinned .infotip__bubble {
  right: auto;
  left: 50%;
  transform: translateX(calc(-50% + var(--infotip-shift, 0px)));
  opacity: 1;
  visibility: visible;
}

/*
 * Arrow: two stacked triangles, the lower one in the border colour and the upper
 * one in the bubble's fill, so the point keeps the bubble's 1px outline instead of
 * reading as a solid dark wedge.
 */
.infotip__bubble::before,
.infotip__bubble::after {
  content: '';
  position: absolute;
  top: 100%;
  /* Stays over the trigger when the balloon is nudged sideways. */
  left: calc(50% - var(--infotip-shift, 0px));
  border: 6px solid transparent;
}

.infotip__bubble::before {
  margin-left: -6px;
  border-top-color: var(--color-border);
}

.infotip__bubble::after {
  margin-left: -6px;
  border-top-color: var(--color-surface-raised);
  margin-top: -1px;
}
</style>
